import type { ReactNode } from 'react';

/**
 * Primitive de badge/pastille (audit UX/UI passe 2, B1). Couvre les tags
 * automatiques/manuels (US-05, RecipeList.tsx) et le badge « à vérifier »
 * des lignes d'ingrédient non reconnues (RecipeForm.tsx) — deux usages qui
 * réimplémentaient chacun `rounded-full px-2 py-0.5 text-xs font-medium …`
 * séparément.
 */
export type BadgeTone = 'auto' | 'manual' | 'warning' | 'neutral';

/**
 * Couleurs des tags, exportées séparément : réutilisées telles quelles par
 * les puces de filtre interactives de RecipeList.tsx, qui ne sont pas des
 * `Badge` (boutons `aria-pressed`, pas des `span`) mais doivent rester
 * visuellement identiques aux badges statiques des cartes.
 */
export const TAG_TONE_CLASSES: Record<'auto' | 'manual', string> = {
  auto: 'bg-tag-auto-bg text-tag-auto',
  manual: 'bg-tag-manual-bg text-tag-manual',
};

const TONE_CLASSES: Record<BadgeTone, string> = {
  auto: TAG_TONE_CLASSES.auto,
  manual: TAG_TONE_CLASSES.manual,
  // Le ton d'alerte était **inversé** : `bg-warning text-warning-bg`, soit un
  // aplat ambre saturé portant un texte crème, là où les trois autres tons
  // font l'inverse (fond doux, texte soutenu). Une pastille « à vérifier »
  // criait donc plus fort que le champ qu'elle commentait.
  warning: 'bg-warning-bg text-warning',
  neutral: 'bg-surface-sunken text-foreground-secondary',
};

/**
 * `rounded-pill` et non `rounded-full` : le jeton existe désormais, et un
 * `rounded-full` recopié à la main est un point de vérité de plus.
 *
 * `px-2.5 py-1` et non `px-2 py-0.5` : à 2 px de padding vertical, une
 * étiquette de 12 px se lit comme un fragment de texte coloré plutôt que
 * comme une pastille. C'est ce que montraient les cartes de la bibliothèque,
 * où « moins de 30 min » flottait sous le titre sans forme propre.
 *
 * `tracking` légèrement ouvert : à cette taille, un texte serré dans une
 * pastille se lit mal.
 */
export function badgeClasses(tone: BadgeTone, className = ''): string {
  return `inline-flex items-center rounded-pill px-2.5 py-1 text-xs font-medium leading-none tracking-[0.01em] ${TONE_CLASSES[tone]} ${className}`.trim();
}

export interface BadgeProps {
  tone: BadgeTone;
  className?: string;
  children: ReactNode;
  /**
   * Ce que la pastille abrège, en toutes lettres — l'info-bulle du navigateur.
   *
   * Ajouté pour le « +N » des cartes de la bibliothèque, qui annonçait un reste
   * sans jamais le nommer. Une pastille qui compte sans dire quoi oblige à
   * ouvrir la fiche pour savoir de quoi elle parle.
   */
  title?: string;
  /**
   * Le même texte pour une synthèse vocale, quand le contenu visible ne suffit
   * pas à comprendre la pastille : « +1 » lu seul ne veut rien dire.
   */
  'aria-label'?: string;
}

export function Badge({
  tone,
  className = '',
  children,
  title,
  'aria-label': ariaLabel,
}: BadgeProps): React.JSX.Element {
  return (
    <span className={badgeClasses(tone, className)} title={title} aria-label={ariaLabel}>
      {children}
    </span>
  );
}
